<script lang="ts" setup>
import type { OfferGroup, OfferImage, OfferProperty, OfferSmall } from '~/types/Catalog';

const isShowSizesModal = defineModel<boolean>({ default: false });

const { selectedOffer, selectedSizeId } = storeToRefs(useCatalogStore());
const { isMobile } = useBreakpoints();

const isShowSizeSelectField = computed(() => {
  const currentGroup = selectedOffer.value.offer_groups?.find((g) => g.id === selectedOffer.value.id);

  return (
    selectedOffer.value.offers.length &&
    currentGroup?.has_balance &&
    selectedOffer.value.offers.some((offer) => offer.offer_properties?.find((p: OfferProperty) => p.name === 'Размер'))
  );
});

function getMainImage(offerGroup: OfferGroup): OfferImage {
  return offerGroup.offer_images.find((image) => image.is_main) ?? offerGroup.offer_images[0];
}

async function handleOfferGroupClick(offerGroup: OfferGroup) {
  return navigateTo(offerGroup.uri);
}

const selectedSizeOffer = ref<OfferSmall | null>();
const route = useRoute();

watch(
  () => selectedSizeOffer.value,
  () => {
    if (selectedSizeOffer.value?.id) {
      window.history.replaceState(
        window.history.state,
        '',
        `${route.path}?${new URLSearchParams({
          ...route.query,
          offer_id: String(selectedSizeOffer.value?.id),
        }).toString()}${route.hash}`,
      );
      selectedSizeId.value = selectedSizeOffer.value?.id;
      selectedOffer.value.offer_balances = selectedSizeOffer.value.offer_balances;
      if (selectedSizeOffer.value.price) selectedOffer.value.price = selectedSizeOffer.value.price;
      if (selectedSizeOffer.value.old_price) selectedOffer.value.old_price = selectedSizeOffer.value.old_price;
    } else {
      window.history.replaceState(window.history.state, '', route.path + route.hash);
      delete selectedOffer.value.offer_balances;
      selectedSizeId.value = null;
    }
  },
);

const sortedSizeOffers = computed(() => {
  const { size_charts_values, offers, size_charts } = selectedOffer.value;

  if (!size_charts_values || size_charts_values.length === 0) return offers;

  const sizeChartId = size_charts_values[0].size_chart_id;

  const chart = size_charts.find((sizeChart) => sizeChart.id === sizeChartId);
  const sizeChartValues = chart ? chart.size_chart_values : [];

  return offers
    .map((offer) => {
      return {
        ...offer,
        order:
          sizeChartValues.find((sizeChartValue) => {
            if (offer.sizes?.length) {
              return offer.sizes[0].value === sizeChartValue.title;
            } else if (offer.offer_properties?.find((p: OfferProperty) => p.name.includes('Размер'))) {
              return (
                offer.offer_properties?.find((p: OfferProperty) => p.name.includes('Размер'))?.value ===
                sizeChartValue.title
              );
            }
          })?.order ?? undefined,
        $isDisabled: offer.balance !== undefined && offer.balance < 1,
      };
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));
});

const sizesListAsString = computed(() => {
  const availableSizes = sortedSizeOffers.value.filter((offer) => !offer.$isDisabled);

  return availableSizes.length
    ? availableSizes
        .map((offer) => {
          if (offer.sizes?.length) return offer.sizes[0].value;
          else return offer.offer_properties?.find((p: OfferProperty) => p.name.includes('Размер'))?.value;
        })
        .join(', ')
    : '';
});

const sizesColumnLabels = computed(() => {
  let labels = [];

  if (sortedSizeOffers.value[0]?.sizes?.length) {
    labels = sortedSizeOffers.value[0]?.sizes;
  } else {
    const sizeLabel = selectedOffer.value.offers[0].offer_properties?.find((p: OfferProperty) =>
      p.name.includes('Размер'),
    );
    if (sizeLabel) labels.push(sizeLabel);
  }

  return labels;
});

function handleClickOnSize(offer: OfferSmall) {
  selectedSizeOffer.value = offer;
  isShowSizesModal.value = false;
}

function handleClick() {
  if (isMobile.value) {
    isShowSizesModal.value = true;
  }
}

onMounted(() => {
  if (route.query.offer_id) {
    selectedSizeOffer.value = selectedOffer.value.offers.find((offer) => offer.id === Number(route.query.offer_id));
  }
});

onUnmounted(() => {
  selectedSizeId.value = null;
});
</script>

<template>
  <section
    v-if="selectedOffer && selectedOffer.offer_groups?.length"
    class="product-page__offers"
  >
    <p>
      Цвет:
      <span class="font-bold">
        {{ selectedOffer.offer_groups.find((g) => g.id === selectedOffer.id)?.color }}
      </span>
    </p>

    <div class="flex gap-2 flex-wrap">
      <button
        v-for="offerGroup in selectedOffer.offer_groups.filter((g) => g.has_balance || g.id === selectedOffer.id)"
        :key="offerGroup.id"
        :class="{ 'product-page__offers-btn--selected': offerGroup.id === selectedOffer.id }"
        class="product-page__offers-btn"
        @click="handleOfferGroupClick(offerGroup)"
      >
        <picture
          v-if="offerGroup.offer_images.length"
          class="w-16 h-16 p-1"
        >
          <source
            :srcset="getMainImage(offerGroup).conversions_webp.thumb_url_180"
            type="image/webp"
          />
          <source
            :srcset="getMainImage(offerGroup).conversions.thumb_url_65"
            type="image/jpeg"
          />
          <img
            :alt="selectedOffer.product_name + offerGroup.color"
            :src="getMainImage(offerGroup).url"
            class="w-full h-full object-contain"
          />
        </picture>
      </button>
    </div>

    <SelectField
      v-if="isShowSizeSelectField"
      :id="selectedOffer.id"
      v-model="selectedSizeOffer"
      :allow-empty="false"
      :field-label="sizesListAsString && `В наличии: ${sizesListAsString}`"
      :options="sortedSizeOffers"
      :searchable="false"
      class="mt-4"
      placeholder="Выберите размер"
      track-by="id"
      @click="handleClick"
    >
      <template #singleLabel>
        {{ selectedSizeOffer?.offer_properties?.find((p: OfferProperty) => p.name.includes('Размер'))?.value || '' }}
      </template>

      <template #beforeList>
        <div class="product-page__offers-select-row p-3">
          <div
            v-for="label in sizesColumnLabels"
            :class="{ 'text-center': sizesColumnLabels.length > 1 }"
            class="flex-1"
          >
            {{ label.name }}
          </div>
        </div>
      </template>

      <template #option="{ option }">
        <div
          v-if="option.sizes?.length"
          class="product-page__offers-select-row"
        >
          <div
            v-for="size in option.sizes"
            :key="size.value"
            :class="{ 'text-center': sizesColumnLabels.length > 1 }"
            class="flex-1 font-bold"
          >
            {{ size.value }}
          </div>
        </div>

        <div v-else>
          {{ option.offer_properties.find((p: OfferProperty) => p.name === 'Размер').value }}
        </div>
      </template>
    </SelectField>

    <BaseModal
      v-model="isShowSizesModal"
      confirm-btn-icon="Cart"
      title="Выберите размер"
      @close="isShowSizesModal = false"
    >
      <div class="product-page__offers-select-row py-3 px-2">
        <div
          v-for="label in sizesColumnLabels"
          :class="{ 'text-center': sizesColumnLabels.length > 1 }"
          class="flex-1"
        >
          {{ label.name }}
        </div>
      </div>

      <button
        v-for="smallOffer in sortedSizeOffers"
        :key="smallOffer.id"
        :disabled="smallOffer.$isDisabled"
        class="product-page__offers-select-row product-page__modal-offer-list-item py-2 last:mb-2"
        @click="handleClickOnSize(smallOffer)"
      >
        <div
          v-for="size in smallOffer.sizes"
          :key="size.value"
          :class="{ 'font-bold': smallOffer.id === selectedSizeOffer?.id, 'text-center': sizesColumnLabels.length > 1 }"
          class="flex-1 font-bold"
        >
          {{ size.value }}
        </div>
      </button>
    </BaseModal>
  </section>
</template>

<style lang="scss" scoped>
.product-page {
  &__offers {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__offers-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__offers-btn {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    min-width: 64px;
    min-height: 40px;
    border: 1px solid transparent;
    border-radius: 12px;
    background-color: $white;
    transition: all 0.3s;

    @apply shadow-sm;

    &:hover {
      border-color: $hell-black-20;
    }

    &--selected {
      border-color: $hell-black-60;
    }
  }

  &__offers-select-row {
    display: flex;
    gap: 8px;
    width: 100%;
    font-size: $text-sm;
    text-align: left;
  }

  &__modal-offer-list-item {
    padding: 8px;
    border-bottom: 1px solid $hell-black-20;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background-color: $hell-black-5;
      color: $hell-black-60;
    }

    &:disabled {
      background-color: $hell-black-5;
      color: $hell-black-40;
      cursor: not-allowed;
    }

    &:last-of-type {
      border: none;
    }
  }
}
</style>
